import { Dialog, Transition } from '@headlessui/react'
import { Fragment, useEffect, useState } from 'react'
import moment from 'moment'
import { ROUTE_PLAYER_PROFILE } from '@/constants/Routes'
import ReliabilityScore from '@/components/common/ReliabilityScore'
import { useGetPlayerProfile } from '@/services/player'
import { useGetClubMatches, useGetClubMemberEntitlements } from '@/services/club'
import { useCanUseCoaching } from '@/hooks/useCanUseCoaching'
import { Match, Team } from '@/types'
import { useClubTools } from '../hooks/useClubTools'
import { Spinner, TabBar, EntitlementIcons } from './ui'
import { CoachingToolCard } from './coaching/CoachingToolCard'

export type MemberDetailTab = 'matches' | 'coaching'

type Props = {
  clubId: number
  userId: number
  onClose: () => void
  /** Which tab to open initially — coaching entry points pass 'coaching'. Falls back to matches. */
  initialTab?: MemberDetailTab
}

const MATCHES_PAGE_SIZE = 20

const ratingText = (value?: string | null) => (!value || value === 'NR' ? '—' : value)

/* A compact rating + reliability readout for the header. */
const HeaderRating = ({
  label,
  value,
  provisional,
  reliability,
}: {
  label: string
  value?: string | null
  provisional?: boolean
  /** 0–100 reliability score; the circle is hidden when 0/absent. */
  reliability?: number
}) => (
  <div className="flex items-center gap-2">
    <div className="text-right">
      <p className="text-[10px] uppercase tracking-wider text-gray-400">{label}</p>
      <p className="text-base font-bold leading-tight text-gray-900">
        {ratingText(value)}
        {provisional && value && value !== 'NR' && (
          <span className="ml-1 align-middle text-[9px] font-medium uppercase text-amber-600">prov</span>
        )}
      </p>
    </div>
    {reliability ? (
      <div className="shrink-0" title="Reliability score">
        <ReliabilityScore progress={reliability} isWhiteBackground />
      </div>
    ) : null}
  </div>
)

/* The member's club entitlement icons for the header — renders nothing when there are none. */
const HeaderEntitlements = ({ clubId, userId }: { clubId: number; userId: number }) => {
  const { data } = useGetClubMemberEntitlements(clubId, {
    userIds: [userId],
    filter: { operations: [], entitlements: [] },
  })

  const entitlements = data?.results?.[0]?.members?.find((m) => m.userId === userId)?.entitlements
  if (!entitlements || Object.keys(entitlements).length === 0) return null

  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] uppercase tracking-wider text-gray-400">Entitlements</span>
      <EntitlementIcons entitlements={entitlements} size="md" />
    </div>
  )
}

/* Pull the played games (game1..game5) out of a match's two teams. */
const getGames = (team1: Team, team2: Team): [number, number][] => {
  const games: [number, number][] = []
  for (let i = 1; i <= 5; i++) {
    const g1 = (team1 as any)[`game${i}`]
    const g2 = (team2 as any)[`game${i}`]
    if (g1 !== undefined && g1 !== -1 && g2 !== undefined && g2 !== -1) {
      games.push([g1, g2])
    }
  }
  return games
}

const formatPlayers = (team: Team): string =>
  team.player2 ? `${team.player1.fullName} & ${team.player2.fullName}` : team.player1.fullName

/* A single compact match row in the member's club match history. */
const MatchRow = ({ match }: { match: Match }) => {
  const hasTeams = Array.isArray(match.teams) && match.teams.length >= 2
  if (!hasTeams) {
    return (
      <li className="flex items-center gap-3 border-b border-gray-50 py-2 last:border-0">
        <span className="w-20 shrink-0 text-[11px] text-gray-500">{moment(match.eventDate).format('MMM D, YYYY')}</span>
        <span className="truncate text-xs text-gray-500">{match.eventName || '—'}</span>
      </li>
    )
  }

  const [team1, team2] = [...match.teams].sort((a, b) => a.serial - b.serial)
  const games = getGames(team1, team2)

  return (
    <li className="flex items-center gap-3 border-b border-gray-50 py-2 last:border-0">
      <span className="w-20 shrink-0 text-[11px] text-gray-500">{moment(match.eventDate).format('MMM D, YYYY')}</span>
      <span
        className={`flex h-5 w-5 shrink-0 items-center justify-center text-[10px] font-bold ${
          match.eventFormat === 'DOUBLES' ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-600'
        }`}
      >
        {match.eventFormat === 'DOUBLES' ? 'D' : 'S'}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs text-gray-800">
          {formatPlayers(team1)}
          <span className="mx-1 text-gray-400">vs</span>
          {formatPlayers(team2)}
        </p>
        {match.eventName && <p className="truncate text-[11px] text-gray-400">{match.eventName}</p>}
      </div>
      <div className="flex shrink-0 gap-1.5">
        {games.map(([g1, g2], i) => (
          <span key={i} className="font-mono text-xs text-gray-700">
            <span className={g1 > g2 ? 'font-semibold' : 'text-gray-400'}>{g1}</span>
            <span className="text-gray-300">-</span>
            <span className={g2 > g1 ? 'font-semibold' : 'text-gray-400'}>{g2}</span>
          </span>
        ))}
      </div>
    </li>
  )
}

/* This member's match history within the club, fetched via the club match endpoint scoped by playerId. */
const MemberMatches = ({ clubId, userId }: { clubId: number; userId: number }) => {
  const { mutate: getClubMatches, data: matchResponse, isLoading } = useGetClubMatches()
  const [matches, setMatches] = useState<Match[]>([])
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [total, setTotal] = useState<number | undefined>(undefined)

  const doFetch = (fetchOffset: number) => {
    getClubMatches({
      clubId,
      filters: { playerId: userId },
      limit: MATCHES_PAGE_SIZE,
      offset: fetchOffset,
      sort: { order: 'DESC', parameter: 'MATCH_DATE' },
    })
  }

  useEffect(() => {
    setMatches([])
    setOffset(0)
    doFetch(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId, userId])

  useEffect(() => {
    if (!matchResponse) return
    setMatches((prev) => (offset === 0 ? matchResponse.result.hits : [...prev, ...matchResponse.result.hits]))
    setHasMore(matchResponse.result.hasMore)
    setTotal(matchResponse.result.total)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchResponse])

  const handleLoadMore = () => {
    const next = offset + MATCHES_PAGE_SIZE
    setOffset(next)
    doFetch(next)
  }

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500">Match history</h4>
        {total !== undefined && <span className="text-[11px] text-gray-400">{total} total</span>}
      </div>

      {isLoading && matches.length === 0 ? (
        <div className="flex justify-center py-6">
          <Spinner size="sm" />
        </div>
      ) : matches.length === 0 ? (
        <p className="py-6 text-center text-xs text-gray-400">No matches in this club yet</p>
      ) : (
        <>
          <ul className="flex flex-col">
            {matches.map((match) => (
              <MatchRow key={match.id} match={match} />
            ))}
          </ul>
          {hasMore && (
            <button
              onClick={handleLoadMore}
              disabled={isLoading}
              className="mt-2 w-full rounded border border-gray-200 py-1.5 text-xs text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {isLoading ? 'Loading…' : 'Load more'}
            </button>
          )}
        </>
      )}
    </div>
  )
}

/**
 * In-club view of a single member — the shared hub reached by clicking a member row (URL-addressable
 * via the `member` query param so a member is shareable/deep-linkable). The header carries the static
 * profile a director wants at a glance (identity, ratings + reliability, entitlements); the body is
 * tabbed:
 *  - Matches: the member's club match history (the default surface).
 *  - Coaching: the coaching tool sections (currently rating adjustment). Shown whenever the viewer can
 *    coach (feature flag / internal); individual tools only render when the club holds the capability,
 *    so a coaching-enabled club without the adjustment capability still gets a working, empty tab.
 * The coaching entry points open this same modal with `initialTab='coaching'`.
 */
export const MemberDetailModal = ({ clubId, userId, onClose, initialTab = 'matches' }: Props) => {
  const { data: player, isLoading } = useGetPlayerProfile(String(userId))

  const canCoach = useCanUseCoaching()
  const { tools, isLoading: toolsLoading } = useClubTools(clubId, canCoach)

  // The coaching tab exists whenever the viewer can coach; the tune tools inside are gated separately.
  const coachingAvailable = canCoach

  const [activeTab, setActiveTab] = useState<MemberDetailTab>(initialTab === 'coaching' ? 'coaching' : 'matches')
  const effectiveTab: MemberDetailTab = activeTab === 'coaching' && !coachingAvailable ? 'matches' : activeTab

  const fullName = player?.fullName || player?.fullname || 'Member'
  const meta = [player?.shortAddress, player?.age ? `Age ${player.age}` : null, player?.hand]
    .filter(Boolean)
    .join(' · ')

  const publicProfileHref = `${ROUTE_PLAYER_PROFILE}/${userId}`

  const tabs = [
    { id: 'matches', label: 'Matches' },
    ...(coachingAvailable ? [{ id: 'coaching', label: 'Coaching' }] : []),
  ]

  return (
    <Transition appear show as={Fragment}>
      <Dialog as="div" className="relative z-50 motion-reduce:transition-none" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              {/* Fixed tool-sized panel — matches the club view's footprint and stays constant across tabs. */}
              <Dialog.Panel
                className="flex w-[95vw] transform flex-col overflow-hidden rounded-xl bg-white text-left align-middle shadow-2xl transition-all sm:w-[85vw] md:w-[75vw] lg:w-[60vw] xl:w-[55vw] 2xl:w-[50vw]"
                style={{ height: 'calc(100vh - 2rem)' }}
              >
                {isLoading ? (
                  <div className="flex flex-1 items-center justify-center">
                    <Spinner />
                  </div>
                ) : (
                  <>
                    {/* Header — identity on the left, ratings + entitlements on the right; shared across tabs */}
                    <div className="relative border-b border-gray-100 px-5 py-4 pr-12">
                      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
                        <div className="flex min-w-0 items-center gap-3">
                          {player?.imageUrl ? (
                            <img src={player.imageUrl} alt="" className="h-12 w-12 shrink-0 rounded-full object-cover" />
                          ) : (
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gray-200 text-base font-semibold text-gray-600">
                              {fullName.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="min-w-0">
                            <Dialog.Title as="h3" className="truncate text-lg font-bold text-gray-900">
                              {fullName}
                            </Dialog.Title>
                            <p className="text-xs text-gray-400">{player?.duprId}</p>
                            {meta && <p className="mt-0.5 truncate text-xs text-gray-500">{meta}</p>}
                            <a
                              href={publicProfileHref}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-medium text-primary-600 hover:text-primary-500"
                            >
                              View public profile
                              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M14 5h5m0 0v5m0-5L10 14M19 13v5a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2h5"
                                />
                              </svg>
                            </a>
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                          <HeaderRating
                            label="Doubles"
                            value={player?.ratings?.doubles}
                            provisional={player?.ratings?.doublesProvisional}
                            reliability={Number(player?.ratings?.doublesReliabilityScore) | 0}
                          />
                          <HeaderRating
                            label="Singles"
                            value={player?.ratings?.singles}
                            provisional={player?.ratings?.singlesProvisional}
                            reliability={Number(player?.ratings?.singlesReliabilityScore) | 0}
                          />
                          <HeaderEntitlements clubId={clubId} userId={userId} />
                        </div>
                      </div>

                      <button
                        onClick={onClose}
                        aria-label="Close"
                        className="absolute right-3 top-3 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    {/* Tabs (only when there is more than one) */}
                    {tabs.length > 1 && (
                      <TabBar
                        tabs={tabs}
                        activeTab={effectiveTab}
                        onTabChange={(id) => setActiveTab(id as MemberDetailTab)}
                        size="sm"
                        className="px-5"
                      />
                    )}

                    {/* Tab content */}
                    <div className="flex-1 overflow-y-auto p-5">
                      <div className="mx-auto max-w-5xl">
                        {effectiveTab === 'matches' && <MemberMatches clubId={clubId} userId={userId} />}

                        {effectiveTab === 'coaching' && (
                          <section>
                            <h4 className="mb-3 text-xs font-bold uppercase tracking-wider text-gray-500">
                              Coaching tools
                            </h4>
                            {toolsLoading ? (
                              <div className="flex justify-center py-6">
                                <Spinner size="sm" />
                              </div>
                            ) : tools.length === 0 ? (
                              <p className="rounded-lg border border-dashed border-gray-200 py-6 text-center text-xs text-gray-400">
                                No coaching tools are available for this club yet.
                              </p>
                            ) : (
                              <div className="flex flex-col gap-3">
                                {tools.map(({ cap, action }) => (
                                  <CoachingToolCard
                                    key={`${cap.key}:${action.key}`}
                                    clubId={clubId}
                                    userId={userId}
                                    duprId={player?.duprId}
                                    fullName={fullName}
                                    capabilityKey={cap.key}
                                    action={action}
                                  />
                                ))}
                              </div>
                            )}
                          </section>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
